/**
 * Firm roles + permissions for the accountant workspace.
 *
 * The person who signs up owns the firm (OWNER / super admin). They invite members and give each a
 * role; a role is a PERMISSION SET, not just a label. Members are then assigned to clients
 * (many-to-many). Everything here is the front-end contract — the backend must enforce the same.
 */

export type RoleKey = 'owner' | 'admin' | 'accountant' | 'reviewer' | 'finance' | 'viewer';

/** Every distinct thing a member can be allowed to do. */
export type Permission =
  | 'firm.manage'        // rename/delete the firm, billing — owner only
  | 'team.manage'        // invite/remove members, change roles
  | 'clients.manage'     // connect clients, assign members
  | 'clients.view'       // see assigned clients' data
  | 'work.review'        // flag, note, raise queries on transactions
  | 'work.signoff'       // lock/sign off a tax year
  | 'billing.manage';    // invoices / subscription

export interface RoleDef {
  key: RoleKey;
  label: string;
  description: string;
  /** Tailwind classes for the role chip. */
  badgeClass: string;
  permissions: Permission[];
}

export const ROLES: Record<RoleKey, RoleDef> = {
  owner: {
    key: 'owner',
    label: 'Owner',
    description: 'Full control of the firm, including billing and team. One per firm.',
    badgeClass: 'bg-primary text-primary-foreground',
    permissions: ['firm.manage', 'team.manage', 'clients.manage', 'clients.view', 'work.review', 'work.signoff', 'billing.manage']
  },
  admin: {
    key: 'admin',
    label: 'Admin',
    description: 'Manage the team and clients. Cannot delete the firm or change billing.',
    badgeClass: 'bg-info/10 text-info',
    permissions: ['team.manage', 'clients.manage', 'clients.view', 'work.review', 'work.signoff']
  },
  accountant: {
    key: 'accountant',
    label: 'Accountant',
    description: 'Prepare and review the books for assigned clients.',
    badgeClass: 'bg-secondary/15 text-secondary',
    permissions: ['clients.view', 'work.review']
  },
  reviewer: {
    key: 'reviewer',
    label: 'Reviewer',
    description: 'Senior review and sign-off on assigned clients.',
    badgeClass: 'bg-accent text-accent-foreground',
    permissions: ['clients.view', 'work.review', 'work.signoff']
  },
  finance: {
    key: 'finance',
    label: 'Finance',
    description: 'Billing and invoices only. No client data.',
    badgeClass: 'bg-warning/15 text-warning',
    permissions: ['billing.manage']
  },
  viewer: {
    key: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to assigned clients.',
    badgeClass: 'bg-muted text-muted-foreground',
    permissions: ['clients.view']
  }
};

/** Ordered list for role pickers (owner excluded — it isn't assignable, only transferable). */
export const ASSIGNABLE_ROLES: RoleDef[] = [
  ROLES.admin,
  ROLES.accountant,
  ROLES.reviewer,
  ROLES.finance,
  ROLES.viewer
];

export type MemberStatus = 'active' | 'invited' | 'suspended';

export interface Member {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: RoleKey;
  status: MemberStatus;
  assignedClients: number;
  joined: string;
}

export const STATUS_META: Record<MemberStatus, { label: string; cls: string }> = {
  active:    { label: 'Active',    cls: 'bg-success/15 text-success' },
  invited:   { label: 'Invited',   cls: 'bg-warning/15 text-warning' },
  suspended: { label: 'Suspended', cls: 'bg-destructive/10 text-destructive' }
};
